import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Eye, EyeOff, Play, Pause, RotateCcw, Clock,
  FileText, Brain, Monitor, Plus, Trash2, Sparkles, Loader2,
  MessageSquare, Lightbulb
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

// Performance Lenses - calm reminders, not evaluation
const PERFORMANCE_LENSES = [
  {
    id: "pace",
    label: "Pace",
    icon: Clock,
    cues: ["Am I rushing?", "Where can I breathe?", "Let the laughs land"]
  },
  {
    id: "presence",
    label: "Presence",
    icon: Eye,
    cues: ["Am I here right now?", "Connect before you speak", "Own the silence"]
  },
  {
    id: "physicality",
    label: "Physicality",
    icon: Play,
    cues: ["Where's my energy?", "Use the space", "Let the body tell the story"]
  },
  {
    id: "breath",
    label: "Breath",
    icon: Pause,
    cues: ["Belly breath before you start", "Exhale tension", "Reset between bits"]
  },
  {
    id: "connection",
    label: "Connection",
    icon: MessageSquare,
    cues: ["See them, don't scan", "Talk to one person", "They want you to win"]
  }
];

// Suggested performance notes
const SUGGESTED_NOTES = [
  "Slow down here",
  "Pause for laugh",
  "Act-out bigger",
  "Eye contact",
  "Drop energy at end",
  "Button strong",
  "Let it breathe",
  "Physicalize this",
  "Punch word: [word]",
  "Callback to earlier"
];

// ============ TELEPROMPTER LITE MODE ============
// Structure-first view: Bit titles → Section titles → Cue words (no raw text)
const TeleprompterLite = ({ setlistData }) => {
  const [currentBitIndex, setCurrentBitIndex] = useState(0);
  const [showCueWords, setShowCueWords] = useState(true); // Toggle between cue words and cue line
  const sectionRefs = useRef({});

  // Get a single cue line (first complete sentence, cleaned up)
  const getCueLine = (content) => {
    if (!content) return "";
    // Get first sentence ending with . ! or ?
    const match = content.match(/^[^.!?]+[.!?]/);
    if (match && match[0].length <= 100) {
      return match[0].trim();
    }
    // Fallback: first line up to 80 chars
    const firstLine = content.split('\n')[0].trim();
    if (firstLine.length <= 80) return firstLine;
    return "";
  };

  // Build structured data: Bits with their sections
  const structuredSet = useMemo(() => {
    return setlistData.bits?.map(setlistBit => {
      const bit = setlistBit.bit_data;
      if (!bit) return null;
      
      const sections = (bit.sections?.filter(s => s.included !== false) || [])
        .sort((a, b) => a.order - b.order)
        .map(section => ({
          sectionId: section.section_id,
          title: section.title || "",
          cueWords: section.cue_words || [],
          // Single cue line: first complete sentence or meaningful phrase
          cueLine: getCueLine(section.content)
        }));
      
      // If no sections, create one from content
      if (sections.length === 0 && bit.content) {
        sections.push({
          sectionId: 'main',
          title: "",
          cueWords: [],
          cueLine: getCueLine(bit.content)
        });
      }
      
      return {
        bitId: bit.bit_id,
        title: bit.title || "Untitled Bit",
        sections
      };
    }).filter(Boolean) || [];
  }, [setlistData]);

  // Scroll to a specific bit
  const scrollToBit = (index) => {
    setCurrentBitIndex(index);
    const bitId = structuredSet[index]?.bitId;
    if (bitId && sectionRefs.current[bitId]) {
      sectionRefs.current[bitId].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (currentBitIndex < structuredSet.length - 1) {
          scrollToBit(currentBitIndex + 1);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentBitIndex > 0) {
          scrollToBit(currentBitIndex - 1);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentBitIndex, structuredSet.length]);

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Monitor className="w-5 h-5 text-primary" />
          <h2 className="font-display text-lg uppercase tracking-wider">Teleprompter Lite</h2>
          <Badge variant="outline" className="ml-2">Structure View</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCueWords(!showCueWords)}
            className="text-sm"
          >
            {showCueWords ? "Show Cue Lines" : "Show Cue Words"}
          </Button>
          <span className="text-sm text-muted-foreground">
            {currentBitIndex + 1} / {structuredSet.length} bits
          </span>
        </div>
      </div>

      {/* Structure View */}
      <ScrollArea className="flex-1">
        <div className="p-8 max-w-2xl mx-auto">
          {structuredSet.map((bit, bitIdx) => (
            <div 
              key={bit.bitId}
              ref={el => sectionRefs.current[bit.bitId] = el}
              className={`mb-12 transition-opacity ${
                bitIdx === currentBitIndex ? 'opacity-100' : 'opacity-40'
              }`}
              onClick={() => scrollToBit(bitIdx)}
            >
              {/* Bit Title */}
              <h2 className="font-display text-3xl uppercase tracking-wider text-primary mb-6 cursor-pointer">
                {bit.title}
              </h2>
              
              {/* Sections */}
              <div className="space-y-6 pl-4 border-l-2 border-primary/30">
                {bit.sections.map((section, secIdx) => (
                  <div key={section.sectionId} className="pl-4">
                    {/* Section Title */}
                    {section.title && (
                      <h3 className="text-xl font-medium text-foreground mb-2">
                        {section.title}
                      </h3>
                    )}
                    {!section.title && bit.sections.length > 1 && (
                      <h3 className="text-lg font-medium text-muted-foreground mb-2">
                        Section {secIdx + 1}
                      </h3>
                    )}
                    
                    {/* Cue Words or Cue Line */}
                    {showCueWords ? (
                      // Cue Words Display
                      section.cueWords.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {section.cueWords.map((word, idx) => (
                            <span 
                              key={idx}
                              className="px-3 py-1 bg-muted text-muted-foreground rounded-full text-lg"
                            >
                              {word}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground/50 text-sm italic">
                          No cue words set
                        </p>
                      )
                    ) : (
                      // Cue Line Display
                      section.cueLine ? (
                        <p className="text-lg text-muted-foreground italic">
                          {section.cueLine}
                        </p>
                      ) : (
                        <p className="text-muted-foreground/50 text-sm italic">
                          No cue line available
                        </p>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* End marker */}
          {structuredSet.length > 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm uppercase tracking-wider">End of Set</p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Navigation Footer */}
      <div className="p-4 border-t border-border flex items-center justify-between flex-shrink-0 bg-muted/30">
        <Button
          variant="outline"
          size="sm"
          onClick={() => scrollToBit(Math.max(0, currentBitIndex - 1))}
          disabled={currentBitIndex === 0}
        >
          <ChevronUp className="w-4 h-4 mr-2" />
          Previous Bit
        </Button>
        
        <span className="text-sm text-muted-foreground">
          ↑↓ or Space to navigate
        </span>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => scrollToBit(Math.min(structuredSet.length - 1, currentBitIndex + 1))}
          disabled={currentBitIndex === structuredSet.length - 1}
        >
          Next Bit
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// ============ MEMORY DRILL MODE ============
const MemoryDrill = ({ setlistData, onUpdateCueWords }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPeeking, setIsPeeking] = useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isGeneratingCues, setIsGeneratingCues] = useState(false);

  // Helper to get first line/sentence for peek functionality
  const getFirstLine = (text) => {
    if (!text) return "";
    const sentenceMatch = text.match(/^[^.!?]+[.!?]/);
    if (sentenceMatch && sentenceMatch[0].length < 150) {
      return sentenceMatch[0];
    }
    const firstLine = text.split('\n')[0];
    return firstLine.length > 100 ? firstLine.slice(0, 100) + "..." : firstLine;
  };

  // Build flat list of sections
  const allSections = useMemo(() => {
    const sections = [];
    setlistData.bits?.forEach(setlistBit => {
      const bit = setlistBit.bit_data;
      if (!bit) return;
      
      const bitSections = bit.sections?.filter(s => s.included !== false) || [];
      if (bitSections.length > 0) {
        bitSections.sort((a, b) => a.order - b.order).forEach(section => {
          sections.push({
            bitId: bit.bit_id,
            bitTitle: bit.title,
            sectionId: section.section_id,
            sectionTitle: section.title,
            content: section.content,
            cueWords: section.cue_words || [],
            performanceNotes: section.performance_notes || [],
            firstLine: getFirstLine(section.content)
          });
        });
      } else if (bit.content) {
        sections.push({
          bitId: bit.bit_id,
          bitTitle: bit.title,
          sectionId: null,
          sectionTitle: "",
          content: bit.content,
          cueWords: [],
          performanceNotes: [],
          firstLine: getFirstLine(bit.content)
        });
      }
    });
    return sections;
  }, [setlistData]);

  const currentSection = allSections[currentIndex] || null;
  const isFirstBitSection = currentIndex === 0 || allSections[currentIndex - 1]?.bitId !== currentSection?.bitId;

  // Timer effect
  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setElapsedSeconds(s => s + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNext = () => {
    if (currentIndex < allSections.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsPeeking(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsPeeking(false);
    }
  };

  const handleGenerateCueWords = async () => {
    if (!currentSection?.sectionId) return;
    setIsGeneratingCues(true);
    
    try {
      const response = await fetch(
        `${API}/bits/${currentSection.bitId}/sections/${currentSection.sectionId}/generate-cue-words`,
        { method: 'POST', credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        onUpdateCueWords?.(currentSection.bitId, currentSection.sectionId, data.cue_words);
        toast.success("Cue words generated");
      }
    } catch (error) {
      toast.error("Failed to generate cue words");
    } finally {
      setIsGeneratingCues(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'p' || e.key === 'P') {
        setIsPeeking(true);
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === 'p' || e.key === 'P') {
        setIsPeeking(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [currentIndex, allSections.length]);

  if (!currentSection) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">No content to rehearse</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-primary" />
          <h2 className="font-display text-lg uppercase tracking-wider">Memory Drill</h2>
          <Badge variant="outline" className="ml-2">
            {currentIndex + 1} / {allSections.length}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {showTimer && (
            <span className="font-mono text-lg mr-2">{formatTime(elapsedSeconds)}</span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setShowTimer(!showTimer);
              if (!showTimer) setElapsedSeconds(0);
            }}
          >
            <Clock className="w-4 h-4" />
          </Button>
          {showTimer && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsTimerRunning(!isTimerRunning)}
            >
              {isTimerRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {/* Bit Title (shown at start of each bit) */}
        {isFirstBitSection && (
          <h3 className="font-display text-3xl uppercase tracking-wider text-primary mb-4">
            {currentSection.bitTitle || "Untitled Bit"}
          </h3>
        )}

        {/* Section Title */}
        {currentSection.sectionTitle && (
          <h4 className="text-xl font-medium text-muted-foreground mb-6">
            {currentSection.sectionTitle}
          </h4>
        )}

        {/* Cue Words */}
        <div className="flex flex-wrap gap-3 justify-center mb-8">
          {currentSection.cueWords.length > 0 ? (
            currentSection.cueWords.map((word, idx) => (
              <span 
                key={idx}
                className="px-4 py-2 bg-primary/20 text-primary rounded-full text-xl font-medium"
              >
                {word}
              </span>
            ))
          ) : (
            <div className="text-center">
              <p className="text-muted-foreground mb-3">No cue words yet</p>
              {currentSection.sectionId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateCueWords}
                  disabled={isGeneratingCues}
                >
                  {isGeneratingCues ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate Cue Words
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Peek Area */}
        <div 
          className={`transition-all duration-200 text-center max-w-2xl ${
            isPeeking ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'
          }`}
        >
          <p className="text-lg text-muted-foreground italic">
            {currentSection.firstLine}
          </p>
        </div>

        {/* Peek Button */}
        <Button
          variant="ghost"
          size="sm"
          className="mt-4 text-muted-foreground"
          onMouseDown={() => setIsPeeking(true)}
          onMouseUp={() => setIsPeeking(false)}
          onMouseLeave={() => setIsPeeking(false)}
          onTouchStart={() => setIsPeeking(true)}
          onTouchEnd={() => setIsPeeking(false)}
        >
          <Eye className="w-4 h-4 mr-2" />
          Hold to Peek
        </Button>

        {/* Performance Notes */}
        {currentSection.performanceNotes.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            {currentSection.performanceNotes.map((note, idx) => (
              <Badge key={idx} variant="secondary" className="text-sm">
                {note}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="p-4 border-t border-border flex items-center justify-between flex-shrink-0">
        <Button
          variant="outline"
          onClick={handlePrev}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>
        
        <span className="text-sm text-muted-foreground">
          Press Space or → for next • Hold P to peek
        </span>
        
        <Button
          onClick={handleNext}
          disabled={currentIndex === allSections.length - 1}
          className="bg-primary text-primary-foreground"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// ============ FULL SCRIPT MODE ============
const FullScript = ({ setlistData }) => {
  // Build full script from all bits
  const fullScript = useMemo(() => {
    const parts = [];
    setlistData.bits?.forEach(setlistBit => {
      const bit = setlistBit.bit_data;
      if (!bit) return;
      
      parts.push({
        type: 'bit',
        title: bit.title,
        bitId: bit.bit_id
      });
      
      const bitSections = bit.sections?.filter(s => s.included !== false) || [];
      if (bitSections.length > 0) {
        bitSections.sort((a, b) => a.order - b.order).forEach(section => {
          parts.push({
            type: 'section',
            title: section.title,
            content: section.content
          });
        });
      } else if (bit.content) {
        parts.push({
          type: 'content',
          content: bit.content
        });
      }
    });
    return parts;
  }, [setlistData]);

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center flex-shrink-0">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-primary" />
          <h2 className="font-display text-lg uppercase tracking-wider">Full Script</h2>
          <Badge variant="outline">Read Only</Badge>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-8 max-w-3xl mx-auto">
          {fullScript.map((part, idx) => {
            if (part.type === 'bit') {
              return (
                <h3 key={idx} className="font-display text-2xl uppercase tracking-wider text-primary mt-8 mb-4 first:mt-0">
                  {part.title || "Untitled Bit"}
                </h3>
              );
            }
            if (part.type === 'section') {
              return (
                <div key={idx} className="mb-6">
                  {part.title && (
                    <h4 className="text-lg font-medium text-muted-foreground mb-2">
                      {part.title}
                    </h4>
                  )}
                  <p className="text-lg leading-relaxed whitespace-pre-wrap font-mono">
                    {part.content}
                  </p>
                </div>
              );
            }
            return (
              <p key={idx} className="text-lg leading-relaxed whitespace-pre-wrap font-mono mb-6">
                {part.content}
              </p>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============ PERFORMANCE INTENT PANEL ============
const PerformanceIntentPanel = ({ 
  setlistData, 
  onUpdateSetlistNotes,
  isCollapsed,
  onToggleCollapse 
}) => {
  const [newNote, setNewNote] = useState("");
  const [expandedLenses, setExpandedLenses] = useState([]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const updatedNotes = [...(setlistData.performance_notes || []), newNote.trim()];
    await onUpdateSetlistNotes(updatedNotes);
    setNewNote("");
  };

  const handleRemoveNote = async (idx) => {
    const updatedNotes = (setlistData.performance_notes || []).filter((_, i) => i !== idx);
    await onUpdateSetlistNotes(updatedNotes);
  };

  const toggleLens = (lensId) => {
    setExpandedLenses(prev => 
      prev.includes(lensId) 
        ? prev.filter(id => id !== lensId)
        : [...prev, lensId]
    );
  };

  return (
    <div className={`border-l border-border bg-card flex flex-col transition-all ${isCollapsed ? 'w-12' : 'w-80'}`}>
      {/* Toggle Button */}
      <button
        onClick={onToggleCollapse}
        className="p-3 border-b border-border hover:bg-muted/50 flex items-center justify-center"
      >
        {isCollapsed ? (
          <ChevronLeft className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>

      {!isCollapsed && (
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-6">
            {/* Delivery Intent Notes */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Delivery Intent
              </h3>
              
              {/* Existing Notes */}
              <div className="space-y-2 mb-3">
                {(setlistData.performance_notes || []).map((note, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-muted/50 rounded-sm group">
                    <span className="flex-1 text-sm">{note}</span>
                    <button
                      onClick={() => handleRemoveNote(idx)}
                      className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add New Note */}
              <div className="flex gap-2">
                <Input
                  placeholder="Add reminder..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                  className="text-sm"
                />
                <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Suggested Notes */}
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-2">Quick add:</p>
                <div className="flex flex-wrap gap-1">
                  {SUGGESTED_NOTES.slice(0, 6).map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setNewNote(suggestion)}
                      className="text-xs px-2 py-1 bg-muted/30 hover:bg-muted rounded-sm"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Performance Lenses */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Performance Lenses
              </h3>
              <div className="space-y-2">
                {PERFORMANCE_LENSES.map((lens) => (
                  <Collapsible
                    key={lens.id}
                    open={expandedLenses.includes(lens.id)}
                    onOpenChange={() => toggleLens(lens.id)}
                  >
                    <CollapsibleTrigger className="w-full flex items-center justify-between p-2 hover:bg-muted/50 rounded-sm">
                      <div className="flex items-center gap-2">
                        <lens.icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{lens.label}</span>
                      </div>
                      {expandedLenses.includes(lens.id) ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-8 pr-2 py-2 space-y-1">
                        {lens.cues.map((cue, idx) => (
                          <p key={idx} className="text-sm text-muted-foreground italic">
                            {cue}
                          </p>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

// ============ MAIN REHEARSAL MODE COMPONENT ============
export default function RehearsalMode({ 
  setlistData, 
  onClose,
  onRefreshSetlist
}) {
  const [mode, setMode] = useState("teleprompter"); // "teleprompter" | "memory" | "script"
  const [isIntentPanelCollapsed, setIsIntentPanelCollapsed] = useState(false);
  const [localSetlistData, setLocalSetlistData] = useState(setlistData);

  // Update local data when prop changes
  useEffect(() => {
    setLocalSetlistData(setlistData);
  }, [setlistData]);

  const handleUpdateSetlistNotes = async (notes) => {
    try {
      const response = await fetch(`${API}/setlists/${localSetlistData.setlist_id}/performance-notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ performance_notes: notes })
      });
      
      if (response.ok) {
        setLocalSetlistData(prev => ({ ...prev, performance_notes: notes }));
        toast.success("Notes updated");
      }
    } catch (error) {
      toast.error("Failed to update notes");
    }
  };

  const handleUpdateCueWords = async (bitId, sectionId, cueWords) => {
    // Update local state
    setLocalSetlistData(prev => {
      const newData = { ...prev };
      newData.bits = newData.bits?.map(b => {
        if (b.bit_data?.bit_id === bitId) {
          return {
            ...b,
            bit_data: {
              ...b.bit_data,
              sections: b.bit_data.sections?.map(s => 
                s.section_id === sectionId 
                  ? { ...s, cue_words: cueWords }
                  : s
              )
            }
          };
        }
        return b;
      });
      return newData;
    });
    
    // Refresh parent data
    onRefreshSetlist?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Top Bar with Back Button and Mode Selector */}
      <div className="border-b border-border p-2 flex items-center justify-between flex-shrink-0 bg-muted/30">
        {/* Back to Setup Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Setup
        </Button>

        {/* Mode Selector - Centered */}
        <div className="flex items-center gap-2">
          <Button
            variant={mode === "teleprompter" ? "default" : "ghost"}
            size="sm"
            onClick={() => setMode("teleprompter")}
            className="gap-2"
          >
            <Monitor className="w-4 h-4" />
            Teleprompter Lite
          </Button>
          <Button
            variant={mode === "memory" ? "default" : "ghost"}
            size="sm"
            onClick={() => setMode("memory")}
            className="gap-2"
          >
            <Brain className="w-4 h-4" />
            Memory Drill
          </Button>
          <Button
            variant={mode === "script" ? "default" : "ghost"}
            size="sm"
            onClick={() => setMode("script")}
            className="gap-2"
          >
            <FileText className="w-4 h-4" />
            Full Script
          </Button>
        </div>

        {/* Setlist Name - Right side */}
        <div className="text-sm text-muted-foreground">
          {localSetlistData.name}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Rehearsal View */}
        <div className="flex-1">
          {mode === "teleprompter" && (
            <TeleprompterLite setlistData={localSetlistData} />
          )}
          {mode === "memory" && (
            <MemoryDrill 
              setlistData={localSetlistData} 
              onUpdateCueWords={handleUpdateCueWords}
            />
          )}
          {mode === "script" && (
            <FullScript setlistData={localSetlistData} />
          )}
        </div>

        {/* Performance Intent Panel */}
        <PerformanceIntentPanel
          setlistData={localSetlistData}
          onUpdateSetlistNotes={handleUpdateSetlistNotes}
          isCollapsed={isIntentPanelCollapsed}
          onToggleCollapse={() => setIsIntentPanelCollapsed(!isIntentPanelCollapsed)}
        />
      </div>
    </div>
  );
}
